import { clearAnalyticsUser } from '@cw-elements/analytics-provider';
import type { AxiosInstance } from 'axios';
import axios, { AxiosHeaders } from 'axios';

import { ENV_VARIABLES } from '@/constants';

import { StorageKeyOptionsEnum } from './auth.constant';
import type { ErrorWithResponse } from './auth.model';

/**
 * Stores data in localStorage.
 *
 * @param key - The key under which the data will be stored in localStorage.
 * @param data - The data to store, which will be serialized to a JSON string.
 */
const setLocalData = (key: string, data: unknown) =>
  localStorage.setItem(key, JSON.stringify(data));

/**
 * Sets a cookie with a specified expiration date.
 *
 * @param name - The name of the cookie.
 * @param value - The value to store in the cookie.
 * @param days - The number of days until the cookie expires.
 */
export const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = 'expires=' + date.toUTCString();
  const secure = globalThis.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax${secure}`;
};

/**
 * Retrieves a cookie value by name.
 *
 * @param name - The name of the cookie to retrieve.
 * @returns The value of the cookie, or null if the cookie does not exist.
 */
export const getCookie = (name: string) => {
  const nameEQ = `${name}=`;
  const cookiesArray = document.cookie.split(';');
  for (const cookie of cookiesArray) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith(nameEQ)) {
      return trimmedCookie.substring(nameEQ.length);
    }
  }
  return null;
};

/**
 * Retrieves data from localStorage.
 *
 * @param key - The key of the data in localStorage.
 * @returns The parsed data from localStorage, or null if not found.
 */
const getLocalData = (key: string) => {
  const data = localStorage.getItem(key) ?? null;

  return data ? JSON.parse(data) : null;
};

/**
 * Removes data from localStorage by key.
 *
 * @param key - The key of the data to remove from localStorage.
 */
const clearLocalData = (key: string) => localStorage.removeItem(key);

/**
 * Removes a cookie by name by setting it to expire in the past.
 *
 * @param name - The name of the cookie to remove.
 */
export const removeCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Clears the stored session token from both localStorage and the cookie.
 * Both must be cleared together: getAccessToken reads the cookie first, so
 * clearing only localStorage would leave a usable token behind after logout.
 */
export const clearAccessData = () => {
  clearLocalData(StorageKeyOptionsEnum.AccessData);
  removeCookie(StorageKeyOptionsEnum.AccessData);
};

/**
 * Retrieves query parameters from a location object.
 *
 * @param location - A partial Location object containing the search string.
 * @returns An object containing the query parameters as key-value pairs.
 */
const getQueryParams = (location: Partial<Location>) => {
  const params = new URLSearchParams(location.search);
  return Object.fromEntries(params.entries());
};

export const getAccessToken = () => {
  const accessData = getLocalData(StorageKeyOptionsEnum.AccessData);
  const cookieAccessData = getCookie(StorageKeyOptionsEnum.AccessData);

  return cookieAccessData ?? accessData?.access_token ?? null;
};

/**
 * Builds the accounts re-authorization URL used when a session expires.
 * Targets the accounts host (not the app origin) and strips dashes from the
 * client id, matching the authorize redirect built in the auth provider.
 */
const buildReauthorizeUrl = () =>
  `${ENV_VARIABLES.ACCOUNTS_URL}/oauth/authorize?response_type=code&client_id=${ENV_VARIABLES.CLIENT_ID?.replaceAll('-', '')}&redirect_uri=${globalThis.location.origin}`;

/**
 * Clears the session token and sends the user to re-authenticate against the
 * accounts host. Analytics identity is dropped alongside the token so the next
 * session does not keep reporting the previous user.
 */
const redirectToReauthorize = () => {
  clearAccessData();
  clearAnalyticsUser();
  globalThis.location.href = buildReauthorizeUrl();
};

const isSessionExpiredError = (error: unknown) => {
  const status = (error as ErrorWithResponse)?.response?.status;
  return status === 401 || status === 403;
};

/**
 * Normalizes an unknown rejection reason to an Error so promise rejections
 * always carry an Error (axios already rejects with an AxiosError, which is
 * passed through unchanged).
 */
const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

/**
 * Attaches the Bearer token to outgoing requests and redirects to
 * re-authentication on 401/403 responses. Shared by the Panorama axios
 * instances so session expiry is handled consistently everywhere.
 */
export const setupAuthInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(config => {
    const token = getAccessToken();
    if (token) {
      config.headers ??= new AxiosHeaders();
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    response => response,
    (error: unknown) => {
      if (isSessionExpiredError(error)) {
        redirectToReauthorize();
      }
      return Promise.reject(toError(error));
    }
  );
};

const api = (clientAuthorizationToken: string) => {
  const accessToken = getAccessToken();
  const instance = axios.create({
    baseURL: import.meta.env.VITE_BASE_API_URL,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      authorization: accessToken
        ? `Bearer ${accessToken}`
        : `Basic ${clientAuthorizationToken}`
    }
  });

  // Add interceptor for global token expiration handling
  instance.interceptors.response.use(
    response => response,
    (error: unknown) => {
      if (isSessionExpiredError(error)) {
        redirectToReauthorize();
      }
      return Promise.reject(toError(error));
    }
  );
  return instance;
};

export { StorageKeyOptionsEnum } from './auth.constant';
export { api, clearLocalData, getLocalData, getQueryParams, setLocalData };
